import {
  CompletedCheck,
  createPreflightCoreApiClient,
  DocumentId,
  DocumentProcess,
  PeerPilotCheck,
} from 'preflight-core';
import { differenceWith } from 'remeda';

import { preflightApiClient } from '../api/client';
import { Check, Journal } from './journal';

const completedChecksCache = new Map<DocumentId, CompletedCheck[]>();
const peerPilotChecksCache = new Map<DocumentId, PeerPilotCheck[]>();

/**
 * Artificially delays the document process so only one check at a time gets
 * marked as completed.
 */
export function getDelayedDocumentProcess(
  documentId: DocumentId,
  groups: Journal['checklist']['groups'],
  isV1 = false,
): Promise<DocumentProcess> {
  const checksOrder = groups.flatMap((group) => group.checks);
  const cachedCompletedChecks = completedChecksCache.get(documentId) ?? [];
  const cachedPeerPilotChecks = peerPilotChecksCache.get(documentId) ?? [];

  return createPreflightCoreApiClient(preflightApiClient)
    .getDocumentProcess(documentId, isV1)
    .then((documentProcess) => {
      if (documentProcess.requestCompleted) {
        return documentProcess;
      } else {
        const newlyCompletedChecks = differenceWith(
          documentProcess.completedChecks,
          cachedCompletedChecks,
          (checkA, checkB) => checkA.checkKey === checkB.checkKey,
        );
        const checkToComplete = getNextCheckToComplete({
          checksOrder,
          completedChecks: cachedCompletedChecks,
          newlyCompletedChecks,
        });

        const newlyPeerPilotChecks = differenceWith(
          documentProcess.peerPilotChecks ?? [],
          cachedPeerPilotChecks,
          (checkA, checkB) => checkA.checkKey === checkB.checkKey,
        );

        if (checkToComplete) {
          completedChecksCache.set(documentId, [
            ...cachedCompletedChecks,
            checkToComplete,
          ]);

          return {
            ...documentProcess,
            completedChecks: cachedCompletedChecks,
            peerPilotChecks: cachedPeerPilotChecks,
          };
        } else {
          if (newlyPeerPilotChecks.length > 0) {
            peerPilotChecksCache.set(documentId, [
              ...cachedPeerPilotChecks,
              ...newlyPeerPilotChecks,
            ]);
          }

          return {
            ...documentProcess,
            completedChecks: cachedCompletedChecks,
            peerPilotChecks: cachedPeerPilotChecks,
          };
        }
      }
    });
}

function getNextCheckToComplete({
  checksOrder,
  completedChecks,
  newlyCompletedChecks,
}: {
  checksOrder: Check['checkKey'][];
  completedChecks: CompletedCheck[];
  newlyCompletedChecks: CompletedCheck[];
}): CompletedCheck | undefined {
  const checkToCompleteKey = checksOrder.find(
    (checkKey) =>
      !completedChecks.some(
        (completedCheck) => checkKey === completedCheck.checkKey,
      ),
  );

  return checkToCompleteKey
    ? newlyCompletedChecks.find(
        (check) => check.checkKey === checkToCompleteKey,
      )
    : undefined;
}
